import Result, { type ResultValue } from './Result';
import { v4 as uuid } from 'uuid';
import type Check from './Check';
import type { CheckSubject } from './Check';

/**
 * Enumeration of the message types.
 */
export const MessageType = {
  INFORMATION: 'Information',
  WARNING: 'Warning',
  ERROR: 'Error',
} as const;

/**
 * One of the message type string values.
 */
export type MessageTypeValue = (typeof MessageType)[keyof typeof MessageType];

/**
 * Mapping of the message type to the result of the corresponding report.
 */
const MESSAGETYPE_TO_RESULT: Record<MessageTypeValue, ResultValue> = {
  [MessageType.INFORMATION]: Result.SUCCESSFUL,
  [MessageType.WARNING]: Result.SUCCESSFUL_WITH_WARNING,
  [MessageType.ERROR]: Result.FAILED,
};

/**
 * A message in a report.
 *
 * Has an id, a type, a message string, a check that generated it and a subject element.
 */
class Message {
  readonly id: string;
  readonly type: MessageTypeValue;
  readonly message: string;
  readonly check?: Check<CheckSubject>;
  readonly subject?: CheckSubject;

  constructor(
    type: MessageTypeValue,
    message: string,
    check?: Check<CheckSubject>,
    subject?: CheckSubject
  ) {
    this.id = uuid();
    this.type = type;
    this.message = message;
    this.check = check;
    this.subject = subject;
  }

  get result(): ResultValue {
    return MESSAGETYPE_TO_RESULT[this.type];
  }
}

export default Message;
